// The `reviews` block's behaviour — written ONCE, run everywhere.
//
// Like the cart and the filter bar: the React surfaces (designer preview, the
// Run view, the site viewer) render the shell (`ReviewsShell` in
// reviewsBlock.tsx) and call `initReviews(root, transport)` from an
// effect; the PUBLISHED STATIC page renders the same shell server-side and
// `reviewsScript()` ships this very function to it, serialised with
// `Function.prototype.toString`. So it must stay closure-free: no imports, no
// module-level helpers, nothing outside its own body.
//
// The server decides everything that matters (which reviews are approved,
// the status a new one is saved with, who may write one) — see
// backend/app/services/record_reviews.py. This only draws what it is told.

export interface ReviewItem {
  rating: number
  title?: string
  body: string
  name: string
  date?: string
  /** The writer passed the block's purchase check. */
  verified?: boolean
}

export interface ReviewsResponse {
  record?: string
  count: number
  average: number
  histogram: Record<string, number>
  reviews: ReviewItem[]
  offset: number
  limit: number
  signed_in?: boolean
  can_submit?: boolean
  submit?: 'signed_in' | 'anyone' | 'off'
  /** Why `can_submit` is false: SIGN_IN_REQUIRED / PURCHASE_REQUIRED /
   *  REVIEWS_CLOSED; `reason_message` is the sentence to show. */
  reason?: string | null
  reason_message?: string
}

export interface ReviewPayload {
  record: string
  rating: number
  title: string
  body: string
  name: string
  hp: string
  elapsed: number
}

/** How a React host talks to the server. The static page passes none and the
 *  function builds its own from the shell's `data-endpoint`. Errors should
 *  carry `.code` (`SIGN_IN_REQUIRED`, …) and a visitor-safe `.message`. */
export interface ReviewsTransport {
  list: (record: string, offset: number) => Promise<ReviewsResponse>
  add: (payload: ReviewPayload) => Promise<{ status: string }>
  /** Sign-in navigation; the static page follows `data-signin-href`. */
  signIn?: () => void
}

/**
 * Wire one `[data-pg-reviews]` shell. Returns nothing; safe to call twice
 * (the second call is a no-op).
 *
 * Markup contract (emitted by `ReviewsShell`):
 *   root  data-pg-reviews, data-endpoint?, data-token-key?, data-record,
 *         data-signin-href?
 *   [data-pg-reviews-summary] [data-pg-reviews-avg] [data-pg-reviews-stars]
 *   [data-pg-reviews-count] [data-pg-reviews-hist]
 *   [data-pg-reviews-list] + <template data-pg-reviews-item> whose
 *   [data-f="stars|title|body|name|date"] are filled per review
 *   [data-pg-reviews-empty] [data-pg-reviews-more]
 *   form[data-pg-reviews-form] (rating radios, title, body, name, _pg_hp)
 *   [data-pg-reviews-name-row] [data-pg-reviews-status]
 *   [data-pg-reviews-signin] [data-pg-reviews-signin-link]
 *   [data-pg-reviews-reason] (why a signed-in shopper cannot write one)
 *   [data-f="verified"] in the item template (shown for a verified purchase)
 */
export function initReviews(root: HTMLElement, transport?: ReviewsTransport | null): void {
  if (!root || root.getAttribute('data-pg-wired') === '1') return
  root.setAttribute('data-pg-wired', '1')
  const q = (sel: string): HTMLElement | null => root.querySelector(sel) as HTMLElement | null
  const record = root.getAttribute('data-record') || ''
  const started = Date.now()
  const stars = (n: number): string => {
    let s = ''
    const r = Math.round(Number(n) || 0)
    for (let i = 1; i <= 5; i++) s += i <= r ? '★' : '☆'
    return s
  }
  const show = (el: HTMLElement | null, on: boolean) => {
    if (!el) return
    if (on) el.removeAttribute('hidden'); else el.setAttribute('hidden', '')
  }

  // ── transport (the static page builds its own) ──
  let tp = transport || null
  if (!tp) {
    const endpoint = root.getAttribute('data-endpoint') || ''
    if (!endpoint) {
      // Nowhere to read from (no API base at publish): nothing to wire.
      show(q('[data-pg-reviews-form]'), false)
      return
    }
    const tokenKey = root.getAttribute('data-token-key') || ''
    const bearer = (): string => {
      try { return tokenKey ? (window.localStorage.getItem(tokenKey) || '') : '' } catch { return '' }
    }
    const fail = (status: number, data: unknown): Error => {
      const d = (data || {}) as { error?: { code?: string; message?: string };
        detail?: { error?: { code?: string; message?: string } } | string }
      const env = d.error || (typeof d.detail === 'object' && d.detail ? d.detail.error : undefined)
      const err = new Error((env && env.message) || (typeof d.detail === 'string' ? d.detail : '')
        || 'Something went wrong — please try again.') as Error & { code?: string; status?: number }
      err.code = (env && env.code) || ''
      err.status = status
      return err
    }
    const call = (method: string, url: string, body?: unknown): Promise<unknown> => {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      const tok = bearer()
      if (tok) headers.Authorization = 'Bearer ' + tok
      return fetch(url, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) })
        .then((r) => r.json().catch(() => ({})).then((d: unknown) => {
          if (!r.ok) throw fail(r.status, d)
          return d
        }))
    }
    const captcha = (): Promise<string | null> => {
      const el = document.querySelector('[data-pg-captcha-key]')
      const key = el && el.getAttribute('data-pg-captcha-key')
      const g = (window as unknown as { grecaptcha?: { ready: (f: () => void) => void;
        execute: (k: string, o: { action: string }) => Promise<string> } }).grecaptcha
      if (!key || !g || !g.execute) return Promise.resolve(null)
      return new Promise((res) => {
        let done = false
        setTimeout(() => { if (!done) { done = true; res(null) } }, 10000)
        try {
          g.ready(() => {
            g.execute(key, { action: 'review' }).then(
              (t) => { if (!done) { done = true; res(t) } },
              () => { if (!done) { done = true; res(null) } })
          })
        } catch { if (!done) { done = true; res(null) } }
      })
    }
    tp = {
      list: (rec, offset) => call('GET', endpoint + '?record=' + encodeURIComponent(rec)
        + '&offset=' + String(offset || 0)) as Promise<ReviewsResponse>,
      add: (payload) => captcha().then((t) => call('POST', endpoint,
        Object.assign({}, payload, t ? { captcha_token: t } : {}))) as Promise<{ status: string }>,
      signIn: () => {
        const href = root.getAttribute('data-signin-href') || '/login'
        const next = window.location.pathname + window.location.search
        window.location.assign(href + (href.indexOf('?') === -1 ? '?' : '&') + 'next=' + encodeURIComponent(next))
      },
    }
  }
  const api = tp as ReviewsTransport

  const list = q('[data-pg-reviews-list]')
  const tpl = root.querySelector('template[data-pg-reviews-item]') as HTMLTemplateElement | null
  const form = q('[data-pg-reviews-form]') as HTMLFormElement | null
  const status = q('[data-pg-reviews-status]')
  const more = q('[data-pg-reviews-more]')
  const signin = q('[data-pg-reviews-signin]')
  let offset = 0
  let total = 0

  const say = (text: string, err?: boolean) => {
    if (!status) return
    status.textContent = text
    status.style.color = err ? 'var(--pg-error, #dc2626)' : 'var(--pg-success, #16a34a)'
    show(status, !!text)
  }
  const item = (r: ReviewItem): Node | null => {
    if (!tpl || !tpl.content) return null
    const node = tpl.content.cloneNode(true) as DocumentFragment
    const set = (f: string, v: string) => {
      const el = node.querySelector('[data-f="' + f + '"]') as HTMLElement | null
      if (!el) return
      el.textContent = v
      if (!v) el.setAttribute('hidden', '')
    }
    set('stars', stars(r.rating))
    const starsEl = node.querySelector('[data-f="stars"]')
    if (starsEl) starsEl.setAttribute('aria-label', String(r.rating) + ' out of 5')
    set('title', r.title || '')
    set('body', r.body || '')
    set('name', r.name || '')
    set('date', r.date || '')
    const ver = node.querySelector('[data-f="verified"]')
    if (ver) { if (r.verified) ver.removeAttribute('hidden'); else ver.setAttribute('hidden', '') }
    return node
  }
  const summary = (d: ReviewsResponse) => {
    const box = q('[data-pg-reviews-summary]')
    if (!box) return
    show(box, d.count > 0)
    const avg = q('[data-pg-reviews-avg]')
    if (avg) avg.textContent = d.count ? (Math.round(d.average * 10) / 10).toFixed(1) : ''
    const st = q('[data-pg-reviews-stars]')
    if (st) { st.textContent = stars(d.average); st.setAttribute('aria-label', String(d.average) + ' out of 5') }
    const cnt = q('[data-pg-reviews-count]')
    if (cnt) cnt.textContent = d.count === 1 ? '1 review' : String(d.count) + ' reviews'
    const hist = q('[data-pg-reviews-hist]')
    if (hist) {
      while (hist.firstChild) hist.removeChild(hist.firstChild)
      for (let i = 5; i >= 1; i--) {
        const n = (d.histogram && d.histogram[String(i)]) || 0
        const row = document.createElement('div')
        row.style.cssText = 'display:flex;align-items:center;gap:8px;font-size:.8rem'
        const lab = document.createElement('span')
        lab.textContent = String(i) + '★'
        lab.style.cssText = 'width:2.2em'
        const bar = document.createElement('span')
        bar.style.cssText = 'flex:1;height:6px;border-radius:3px;background:var(--pg-border,#e2e8f0);overflow:hidden;display:block'
        const fill = document.createElement('span')
        fill.style.cssText = 'display:block;height:100%;background:var(--pg-accent,var(--pg-primary,#f59e0b));width:'
          + String(d.count ? Math.round((n / d.count) * 100) : 0) + '%'
        bar.appendChild(fill)
        const c = document.createElement('span')
        c.textContent = String(n)
        c.style.cssText = 'width:2em;text-align:right;color:var(--pg-text-secondary,#64748b)'
        row.appendChild(lab); row.appendChild(bar); row.appendChild(c)
        hist.appendChild(row)
      }
    }
  }
  const reasonEl = q('[data-pg-reviews-reason]')
  const access = (d: ReviewsResponse) => {
    const canWrite = !!d.can_submit
    show(form, canWrite)
    show(signin, !canWrite && (d.reason === 'SIGN_IN_REQUIRED'
      || (!d.reason && d.submit === 'signed_in' && !d.signed_in)))
    const why = !canWrite && d.reason === 'PURCHASE_REQUIRED'
      ? (d.reason_message || 'Only customers who bought this item can review it.') : ''
    if (reasonEl) { reasonEl.textContent = why; show(reasonEl, !!why) }
    const nameRow = q('[data-pg-reviews-name-row]')
    show(nameRow, canWrite && !d.signed_in)
  }
  const load = (append: boolean) => {
    if (!record || record.indexOf('{{') !== -1) return Promise.resolve()
    return api.list(record, append ? offset : 0).then((d) => {
      if (!append && list) while (list.firstChild) list.removeChild(list.firstChild)
      const rows = (d && d.reviews) || []
      for (let i = 0; i < rows.length; i++) {
        const n = item(rows[i])
        if (n && list) list.appendChild(n)
      }
      total = d.count || 0
      offset = (append ? offset : 0) + rows.length
      summary(d)
      access(d)
      show(q('[data-pg-reviews-empty]'), total === 0)
      show(more, offset < total)
    }).catch(() => {
      show(q('[data-pg-reviews-empty]'), true)
    })
  }
  if (more) more.addEventListener('click', () => { void load(true) })
  const link = q('[data-pg-reviews-signin-link]')
  if (link) {
    link.addEventListener('click', (e) => {
      if (!api.signIn) return
      e.preventDefault()
      api.signIn()
    })
  }
  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault()
      const els = form.elements as unknown as Record<string, HTMLInputElement & RadioNodeList>
      const ratingRaw = els.rating ? els.rating.value : ''
      const rating = parseInt(String(ratingRaw || '0'), 10)
      if (!(rating >= 1 && rating <= 5)) { say('Choose a rating from 1 to 5 stars.', true); return }
      const body = els.body ? String(els.body.value || '').trim() : ''
      if (!body) { say('Write a few words about it.', true); return }
      const btn = form.querySelector('[type=submit]') as HTMLButtonElement | null
      if (btn) btn.disabled = true
      say('')
      api.add({
        record, rating, body,
        title: els.title ? String(els.title.value || '') : '',
        name: els.name ? String(els.name.value || '') : '',
        hp: els._pg_hp ? String(els._pg_hp.value || '') : '',
        elapsed: (Date.now() - started) / 1000,
      }).then((res) => {
        form.reset()
        if (res && res.status === 'approved') {
          say('Thanks — your review is published.')
          void load(false)
        } else {
          say('Thanks — your review will appear once it is approved.')
        }
      }).catch((err: Error & { code?: string }) => {
        if (err && (err.code === 'SIGN_IN_REQUIRED' || err.code === 'SESSION_EXPIRED')) {
          show(form, false)
          show(signin, true)
        } else if (err && err.code === 'PURCHASE_REQUIRED') {
          show(form, false)
          if (reasonEl) { reasonEl.textContent = err.message; show(reasonEl, true) }
          return
        }
        say((err && err.message) || 'Could not post your review — please try again.', true)
      }).then(() => { if (btn) btn.disabled = false })
    })
  }
  void load(false)
}

/** The same wiring for the published static page (no React on it). */
export function reviewsScript(): string {
  return `(function(){
  if(window.__pgReviews)return;window.__pgReviews=1;
  var init=${initReviews.toString()};
  function go(){var b=document.querySelectorAll('[data-pg-reviews]');for(var i=0;i<b.length;i++){try{init(b[i])}catch(e){}}}
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',go);else go();
})();`
}
